import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Tabs,
  Tab,
  Divider,
  Alert,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector } from "react-redux";
import Wrapper from "../../../components/Wrapper";
import BankDetail from "../BankDetail/BankDetail";
import CancellationPolicyBox from "../../../components/CancellationPolicyBox";
import title from "../../../components/title";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { BASE_URL } from "../../../config/config";

export default function Wallet() {
  title("Wallet");
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const isBank = validateUser?.UserValidate?.data?.user?.is_bank || false;

  const [tab, setTab] = useState(0);

  // available = what walletBalance actually is server-side (withdrawable
  // right now). pending = verified online payments still waiting on the
  // tenant's move-in — shown so the owner can see the money exists, but it
  // is NOT part of `available` and cannot be withdrawn yet. See
  // GetWalletSummary (WithdrawRequest.js) and confirmMoveIn/cancelBooking
  // (flat-interest controller) for why the split sits there and not here.
  const [walletSummary, setWalletSummary] = useState({
    available: 0,
    pending: 0,
    pendingBreakdown: [],
    held: [],
    claimable: [],
    claimed: [],
  });
  const [claimingId, setClaimingId] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const walletBalance = walletSummary.available;

  const fetchWalletSummary = useCallback(async () => {
    const userId =
      validateUser?.UserValidate?.data?.user?._id ||
      sessionStorage.getItem("user_id") ||
      localStorage.getItem("user_id");
    if (!userId) return;

    try {
      const res = await axios.get(`${BASE_URL}/wallet-summary/${userId}`);
      if (res?.data?.success) {
        setWalletSummary(res.data.data);
      }
    } catch (error) {
      toast.error("We could not load your wallet. Please refresh the page.");
    } finally {
      setSummaryLoading(false);
    }
  }, [validateUser]);

  useEffect(() => {
    fetchWalletSummary();
  }, [fetchWalletSummary]);

  // Ask OTS to release your share of one online booking payment.
  const claimPayout = async (requestId) => {
    if (!isBank) {
      toast.error("Please add your bank account details before claiming a payment.");
      setTab(0);
      return;
    }
    setClaimingId(requestId);
    try {
      const res = await axios.post(`${BASE_URL}/claim-payout/${requestId}`);
      toast.success(res.data?.message || "Your claim has been sent to OTS.");
      fetchWalletSummary();
    } catch (error) {
      toast.error(error.response?.data?.message || "We could not send your claim. Please try again.");
      fetchWalletSummary();
    } finally {
      setClaimingId("");
    }
  };

  // Bank account state (demo)
  const [bankAccount, setBankAccount] = useState(null);

  // Withdraw state
  const [withdrawAmount, setWithdrawAmount] = useState("");

  const handleTabChange = (e, newValue) => {
    setTab(newValue);

    if (newValue === 3) {
      fetchMyRequests();
    }
  };
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const handleAddBank = () => {
    setBankAccount({
      bankName: "Meezan Bank",
      accountTitle: "Muhammad Ali",
      accountNumber: "1234567890",
    });
  };

  const fetchMyRequests = async () => {
    try {
      setLoadingRequests(true);

      const userId =
        validateUser?.UserValidate?.data?.user?._id ||
        sessionStorage.getItem("user_id") ||
        localStorage.getItem("user_id");

      const res = await axios.get(`${BASE_URL}/withdrawal-request/${userId}`);

      if (res?.data?.success) {
        setMyRequests(res.data.data);
      }
    } catch (error) {
      toast.error("We could not load your requests. Please try again.");
    } finally {
      setLoadingRequests(false);
    }
  };
  const thStyle = {
    padding: "10px",
    borderBottom: "1px solid #ddd",
    textAlign: "left",
  };

  const tdStyle = {
    padding: "10px",
    borderBottom: "1px solid #eee",
  };

  return (
    <>
      <Wrapper />
      <div
        className={`main-content ${open ? "sidebar-open" : "sidebar-closed"}`}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography variant="h4" fontWeight={700} mb={3}>
            Wallet
          </Typography>

          {/* Available vs Pending — the whole point is that these are two
              different numbers. A cash payment never appears in either one,
              since OTS never holds cash to begin with. */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Available to Withdraw
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {summaryLoading ? "…" : `PKR ${walletSummary.available.toLocaleString()}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Money OTS already owes you, such as your share when a tenant
                    cancels. Claims on tenant payments are made below.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: 3, height: "100%", bgcolor: "#fffaf0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HourglassEmptyIcon fontSize="small" sx={{ color: "warning.main" }} />
                    <Typography variant="body2" color="text.secondary">
                      Held by OTS
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {summaryLoading ? "…" : `PKR ${walletSummary.pending.toLocaleString()}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Paid by tenants online and safe with OTS. Your share (the
                    advance — OTS's commission is not part of it) can be
                    claimed when the booking's refund period ends, or as soon
                    as the tenant moves in. Each payment below shows its date.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {(walletSummary.claimable.length > 0 ||
            walletSummary.held.length > 0 ||
            walletSummary.claimed.length > 0) && (
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Tenant payments held by OTS
                </Typography>
                <Box sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f5f5f5" }}>
                        <th style={thStyle}>Property</th>
                        <th style={thStyle}>Tenant</th>
                        <th style={thStyle}>Your share</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle} />
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ...walletSummary.claimable.map((r) => ({ ...r, state: "claimable" })),
                        ...walletSummary.held.map((r) => ({ ...r, state: "held" })),
                        ...walletSummary.claimed.map((r) => ({ ...r, state: "claimed" })),
                      ].map((r) => (
                        <tr key={r.requestId}>
                          <td style={tdStyle}>{r.flatName}</td>
                          <td style={tdStyle}>{r.tenantName}</td>
                          <td style={tdStyle}>
                            <b>PKR {r.amount.toLocaleString()}</b>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              Tenant paid PKR {r.paidAmount.toLocaleString()}; OTS commission
                              PKR {r.commissionAmount.toLocaleString()} is not included
                            </div>
                          </td>
                          <td style={tdStyle}>
                            {r.state === "claimable" && (
                              <span style={{ color: "green", fontWeight: 600 }}>Ready to claim</span>
                            )}
                            {r.ifCancelledNow && r.ifCancelledNow.cancellable && (
                              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                                If the tenant cancels now: tenant gets PKR{" "}
                                {r.ifCancelledNow.tenantGets.toLocaleString()}, you get PKR{" "}
                                {r.ifCancelledNow.ownerGets.toLocaleString()}
                              </div>
                            )}
                            {r.state === "held" && (
                              <span style={{ color: "orange", fontWeight: 600 }}>
                                Held
                                {r.claimableAt
                                  ? ` — claim from ${new Date(r.claimableAt).toLocaleString()}`
                                  : ""}
                              </span>
                            )}
                            {r.state === "claimed" && (
                              <span style={{ color: "#1976d2", fontWeight: 600 }}>
                                Claim sent — OTS is reviewing
                              </span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            {r.state === "claimable" && (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={Boolean(claimingId)}
                                onClick={() => claimPayout(r.requestId)}
                                startIcon={
                                  claimingId === r.requestId ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : null
                                }
                              >
                                {claimingId === r.requestId ? "Sending claim…" : "Claim payment"}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>
                  If a tenant cancels before you claim
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  While a payment is within its refund period, a cancelling tenant is refunded
                  automatically from the held money, as below, and your share of what is deducted
                  goes to your wallet. When the refund period ends, or once the tenant moves in, a
                  cancellation no longer affects your payment and you can claim it. Each booking
                  follows the policy that was live when the tenant paid.
                </Typography>
                <CancellationPolicyBox
                  steps={(walletSummary.held[0] || walletSummary.claimable[0] || walletSummary.claimed[0])?.ladder}
                  dense
                />
              </CardContent>
            </Card>
          )}

          <Card sx={{ borderRadius: 3 }}>
            {/* Tabs */}
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              <Tab label="Bank Accounts" />
              <Tab label="Credit / Debit" disabled={true} />
              <Tab label="Withdraw Requests" />
              <Tab label="My Requests" />
            </Tabs>

            <Divider />

            <CardContent>
              {/* 1️⃣ Bank Account Tab */}
              {tab === 0 && (
                <Box>
                  {/* {!bankAccount ? (
                    <Box>
                      <Typography mb={2} color="text.secondary">
                        Add your bank account to enable withdrawals.
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField fullWidth label="Bank Name" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField fullWidth label="Account Title" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField fullWidth label="Account Number" />
                        </Grid>
                      </Grid>

                      <Button
                        variant="contained"
                        sx={{ mt: 2, bgcolor: "black" }}
                        onClick={handleAddBank}
                      >
                        Save Bank Account
                      </Button>
                    </Box>
                  ) : (
                    <Card
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "#f9f9f9",
                      }}
                    >
                      <Typography fontWeight={600}>
                        {bankAccount.bankName}
                      </Typography>
                      <Typography>Title: {bankAccount.accountTitle}</Typography>
                      <Typography>
                        Account No: {bankAccount.accountNumber}
                      </Typography>
                    </Card>
                  )} */}
                  <BankDetail />
                </Box>
              )}

              {/* 2️⃣ Credit / Debit Tab */}
              {tab === 1 && (
                <Box>
                  {[
                    { type: "credit", amount: 2000, date: "2026-01-20" },
                    { type: "debit", amount: 500, date: "2026-01-18" },
                  ].map((tx, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1.2,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <Typography>
                        {tx.type === "credit" ? "Credit" : "Debit"}
                      </Typography>
                      <Typography
                        color={tx.type === "credit" ? "green" : "red"}
                        fontWeight={600}
                      >
                        {tx.type === "credit" ? "+" : "-"} PKR {tx.amount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* 3️⃣ Withdraw Requests */}
              {tab === 2 && (
                <Box>
                  {/* <Typography mb={2} fontWeight={600}>
                    Withdraw Request
                  </Typography> */}
                  {!isBank ? (
                    // 👉 BANK DETAILS NOT ADDED
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: "1px dashed #ccc",
                        textAlign: "center",
                        bgcolor: "#fafafa",
                      }}
                    >
                      {/* <AccountBalanceIcon sx={{ fontSize: 40, color: "text.secondary" }} /> */}

                      <Typography mt={2} fontWeight={600}>
                        Bank Details Required
                      </Typography>

                      <Typography
                        mt={1}
                        mb={2}
                        color="text.secondary"
                        fontSize={14}
                      >
                        To withdraw your wallet balance, please add your bank
                        account details first.
                      </Typography>

                      <Button
                        variant="contained"
                        sx={{ bgcolor: "black" }}
                        onClick={() => {
                          // 👉 open the bank details page / modal here
                          // navigate("/add-bank-details");
                          // ya: setOpenBankModal(true)
                          setTab(0);
                        }}
                      >
                        Add Bank Details
                      </Button>
                    </Card>
                  ) : (
                    <>
                      <Typography mb={1} color="text.secondary">
                        Wallet Balance: <b>PKR {walletBalance}</b>
                      </Typography>
                      {walletSummary.pending > 0 && (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                          You also have PKR {walletSummary.pending.toLocaleString()}{" "}
                          held by OTS from tenant payments — see "Tenant payments
                          held by OTS" above. It isn't included here.
                        </Alert>
                      )}

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Enter Amount"
                            type="text" // ✅ IMPORTANT
                            value={withdrawAmount}
                            onChange={(e) => {
                              const value = e.target.value;
                              // numbers only
                              if (/^\d*$/.test(value)) {
                                setWithdrawAmount(value);
                              }
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Button
                        variant="contained"
                        startIcon={
                          withdrawing ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <ArrowDownwardIcon />
                          )
                        }
                        sx={{ mt: 2, bgcolor: "black" }}
                        disabled={
                          withdrawing || !withdrawAmount || Number(withdrawAmount) <= 0
                        }
                        onClick={async () => {
                          const userId =
                            sessionStorage.getItem("user_id") ||
                            localStorage.getItem("user_id");

                          const amount = Number(withdrawAmount);

                          // ✅ validation
                          if (!amount || amount <= 0) {
                            toast.error("Please enter a valid amount.");
                            setWithdrawAmount("");
                            return;
                          }

                          if (amount > walletBalance) {
                            toast.error("Your wallet balance is not enough for this amount.");
                            setWithdrawAmount("");
                            return;
                          }

                          setWithdrawing(true);
                          try {
                            const res = await axios.post(
                              `${BASE_URL}/withdraw-request`,
                              {
                                userId,
                                amount,
                              }
                            );

                            if (res?.data?.success) {
                              toast.success(
                                res.data?.message ||
                                  "Your withdrawal request has been sent."
                              );
                              setWithdrawAmount("");
                              fetchWalletSummary();
                            } else {
                              toast.error(
                                res?.data?.message || "We could not process your withdrawal request."
                              );
                              setWithdrawAmount("");
                            }
                          } catch (error) {
                            toast.error(
                              error?.response?.data?.message ||
                                "Something went wrong. Please try again."
                            );
                            setWithdrawAmount("");
                          } finally {
                            setWithdrawing(false);
                          }
                        }}
                      >
                        {withdrawing ? "Sending request…" : "Request withdrawal"}
                      </Button>
                    </>
                  )}
                </Box>
              )}

              {/* 4️⃣ Approved Requests */}
              {tab === 3 && (
                <Box>
                  <Typography mb={2} fontWeight={600}>
                    My Withdraw Requests
                  </Typography>

                  {loadingRequests ? (
                    <Typography>Loading...</Typography>
                  ) : myRequests.length === 0 ? (
                    <Typography color="text.secondary">
                      No withdraw requests found
                    </Typography>
                  ) : (
                    <Box sx={{ overflowX: "auto" }}>
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr style={{ background: "#f5f5f5" }}>
                            <th style={thStyle}>#</th>
                            <th style={thStyle}>Amount</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myRequests.map((req, index) => (
                            <tr key={req._id}>
                              <td style={tdStyle}>{index + 1}</td>
                              <td style={tdStyle}>PKR {req.amount}</td>
                              <td style={tdStyle}>
                                <span
                                  style={{
                                    color:
                                      req.status === "approved"
                                        ? "green"
                                        : req.status === "rejected"
                                        ? "red"
                                        : req.status === "void"
                                        ? "gray"
                                        : "orange",
                                    fontWeight: 600,
                                  }}
                                >
                                  {{
                                    pending: "pending",
                                    ready: "being prepared",
                                    approved: "released",
                                    rejected: "declined",
                                    void: "voided (booking cancelled)",
                                  }[req.status] || req.status}
                                </span>
                                {req.kind === "booking" && req.flatName && (
                                  <div style={{ fontSize: 12, color: "#666" }}>
                                    {req.flatName}
                                  </div>
                                )}
                                {req.status === "rejected" && req.reason && (
                                  <div style={{ fontSize: 12, color: "#666" }}>{req.reason}</div>
                                )}
                              </td>
                              <td style={tdStyle}>
                                {new Date(req.createdAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}
